import { produce } from "immer";

import { BaseModel } from "../../core/models";
import { add, dayMs, getTimezoneOffsetInMilliseconds, secondMs, toUTCDate } from "../../core/models/DateExtensions";
import { type GoogleProtobufTimestamp, type InputSchedule } from "../../../gql-generated/types";
import { DateConvert } from "../../lenses/domain/services/DateConvert";

import { RangeModifier } from "./RangeModifier";

export interface ScheduleData {
    scheduleStart: Partial<GoogleProtobufTimestamp>;
    scheduleEnd: Partial<GoogleProtobufTimestamp>;
    useClientTimezone: boolean;
}

export class RangeModifierOperations {
    static toSchedule(range: RangeModifier): InputSchedule {
        if (RangeModifierOperations.isNullOrDefault(range)) {
            return {
                scheduleEnd: null,
                scheduleStart: null,
                useClientTimezone: false,
            };
        } else {
            const utcInclusiveRange = RangeModifierOperations.toUTCInclusive(range);

            return {
                scheduleEnd: DateConvert.toTimestamp(utcInclusiveRange.to as Date),
                scheduleStart: DateConvert.toTimestamp(utcInclusiveRange.from as Date),
                useClientTimezone: utcInclusiveRange.useClientUserLocalTimezone,
            };
        }
    }

    static fromSchedule(schedule: ScheduleData | null | undefined): RangeModifier {
        if (!schedule) {
            return new RangeModifier();
        }
        const { scheduleEnd, scheduleStart, useClientTimezone } = schedule;
        if (!scheduleEnd || !scheduleStart || (scheduleEnd.seconds === 0 && scheduleStart.seconds === 0)) {
            return new RangeModifier();
        }

        return RangeModifierOperations.fromUTCInclusive(
            BaseModel.createInstance(RangeModifier, {
                from: DateConvert.toDate(scheduleStart),
                to: DateConvert.toDate(scheduleEnd),
                useClientUserLocalTimezone: useClientTimezone,
            }) as RangeModifier,
        );
    }

    static toUTCInclusive(range: RangeModifier) {
        if (RangeModifierOperations.isNullOrDefault(range)) {
            return range;
        }

        return produce(range, (draft) => {
            draft.from = toUTCDate(range.from as Date);
            draft.to = add(toUTCDate(range.to as Date), dayMs - secondMs);
        });
    }

    private static fromUTCInclusive(range: RangeModifier) {
        return produce(range, (draft) => {
            draft.from = add(range.from as Date, dayMs / 2 + getTimezoneOffsetInMilliseconds());
            draft.to = add(range.to as Date, -dayMs / 2 + getTimezoneOffsetInMilliseconds() + secondMs);
        });
    }

    private static isNullOrDefault({ from, to }: RangeModifier) {
        return from == null || to == null || (from.getTime() === 0 && to.getTime() === 0);
    }
}
